import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, RefreshCw, Check, AlertCircle, Upload } from "lucide-react";

/**
 * WebcamCapture — opens device webcam, lets user frame + snap a photo,
 * then returns it as a File via onCapture(file). Closes via onClose().
 *
 * When the browser blocks camera access (permission denied, no camera,
 * insecure origin, etc.), the modal must NOT leave the user stuck.
 * Every error state renders an explicit "Upload from files" action that
 * calls `onUploadInstead()` — the parent closes the modal and opens the
 * native file picker in the same gesture chain. This is critical on
 * desktop Chrome / macOS where the permission prompt is per-origin and
 * denials are sticky.
 *
 * Used on desktop / laptop. Mobile / tablet use <input capture="environment">.
 */
export default function WebcamCapture({ open, onCapture, onClose, onUploadInstead }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [snapped, setSnapped] = useState(null); // dataUrl preview

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setError("");
      setSnapped(null);
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("This browser does not support camera access. Try Upload From Files instead.");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (e) {
        if (e?.name === "NotAllowedError") {
          setError("Camera access was blocked for this site. Choose Upload from files below, or allow camera access in your browser settings.");
        } else if (e?.name === "NotFoundError") {
          setError("No camera was found on this device. Choose Upload from files below to add a photo.");
        } else {
          setError("The camera could not start on this device. Choose Upload from files below to add a photo.");
        }
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open]);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    setSnapped(canvas.toDataURL("image/jpeg", 0.92));
  };

  const useThisPhoto = async () => {
    if (!snapped) return;
    const blob = await (await fetch(snapped)).blob();
    const file = new File([blob], `colourmatch-${Date.now()}.jpg`, { type: "image/jpeg" });
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    onCapture(file);
  };

  const retake = () => setSnapped(null);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="webcam-modal"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white rounded-3xl overflow-hidden w-full max-w-2xl shadow-floating"
          >
            <div className="px-5 py-4 flex items-center justify-between border-b border-brand-border">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-brand-teal">Take photo</div>
                <h3 className="text-lg font-bold text-brand-ink">Camera</h3>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-brand-sand flex items-center justify-center text-brand-muted" data-testid="webcam-close-btn">
                <X size={18} />
              </button>
            </div>

            <div className="relative aspect-[4/3] bg-black">
              {error ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-white">
                  <AlertCircle size={32} className="mb-3 text-amber-400" />
                  <p className="text-sm max-w-sm mb-5" data-testid="webcam-error">{error}</p>
                  {onUploadInstead && (
                    <button
                      type="button"
                      onClick={onUploadInstead}
                      data-testid="webcam-upload-instead-btn"
                      className="inline-flex items-center justify-center gap-2 bg-white text-brand-ink h-12 px-6 rounded-2xl text-sm font-semibold hover:bg-brand-sand transition-all tap-spring shadow-soft"
                    >
                      <Upload size={16} /> Upload from files
                    </button>
                  )}
                </div>
              ) : snapped ? (
                <img src={snapped} alt="Captured" className="w-full h-full object-contain" data-testid="webcam-snapped" />
              ) : (
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="webcam-video" />
              )}
            </div>

            <div className="px-5 py-4">
              {error ? (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] text-brand-muted flex-1 leading-relaxed">
                    Uploading a photo works exactly the same — the camera is optional.
                  </p>
                  <button
                    onClick={onClose}
                    data-testid="webcam-close-after-error-btn"
                    className="inline-flex items-center gap-1.5 h-11 px-4 rounded-2xl text-sm font-semibold text-brand-muted hover:bg-brand-sand transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : snapped ? (
                <div className="flex items-center justify-between gap-3">
                  <button onClick={retake} data-testid="webcam-retake-btn"
                    className="inline-flex items-center gap-2 bg-brand-sand text-brand-ink h-12 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-border transition-all tap-spring">
                    <RefreshCw size={15} /> Retake
                  </button>
                  <button onClick={useThisPhoto} data-testid="webcam-use-btn"
                    className="inline-flex items-center gap-2 bg-brand-navy text-white h-12 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 transition-all tap-spring">
                    <Check size={15} /> Use this photo
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <p className="text-xs text-brand-muted">Hold steady. We&apos;ll crop later.</p>
                  <button onClick={snap} disabled={!!error}
                    data-testid="webcam-snap-btn"
                    aria-label="Take photo"
                    className="inline-flex items-center justify-center bg-brand-navy text-white h-16 w-16 rounded-full hover:bg-brand-navy/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all tap-spring shadow-navy ring-4 ring-brand-teal/15">
                    <Camera size={24} />
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
